import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Building2,
  Users,
  Server,
  Package,
  Receipt,
  Clock,
  CreditCard,
  Wallet,
  TrendingUp,
  Mail,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/stores/sidebarStore';
import { RoutesConfig } from '@/navigation/routes';

const navItems = [
  { key: 'dashboard', icon: LayoutDashboard, path: RoutesConfig.Dashboard.fullPath },
  { key: 'companies', icon: Building2, path: RoutesConfig.Companies.fullPath },
  { key: 'users', icon: Users, path: RoutesConfig.Users.fullPath },
  { key: 'machines', icon: Server, path: RoutesConfig.Machines.fullPath },
  { key: 'products', icon: Package, path: RoutesConfig.Products.fullPath },
  { key: 'transactions', icon: Receipt, path: RoutesConfig.Transactions.fullPath },
  { key: 'sessions', icon: Clock, path: RoutesConfig.Sessions.fullPath },
  { key: 'topupOrders', icon: CreditCard, path: RoutesConfig.TopupOrders.fullPath },
  { key: 'wallets', icon: Wallet, path: RoutesConfig.Wallets.fullPath },
  { key: 'revenue', icon: TrendingUp, path: RoutesConfig.Revenue.fullPath },
  { key: 'emails', icon: Mail, path: RoutesConfig.Emails.fullPath },
  { key: 'settings', icon: Settings, path: RoutesConfig.Settings.fullPath },
];

export function Sidebar() {
  const { t } = useTranslation();
  const { isCollapsed, toggleCollapsed } = useSidebarStore();

  return (
    <aside
      className={cn(
        'hidden lg:flex flex-col border-r bg-card h-screen sticky top-0 transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/logo-256.png" alt="Vendly" className="w-8 h-8 rounded-lg shrink-0" />
          {!isCollapsed && (
            <span className="font-bold text-lg text-primary truncate">VendWallet</span>
          )}
        </div>
        <button
          onClick={toggleCollapsed}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    isCollapsed && 'justify-center px-2'
                  )
                }
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!isCollapsed && <span>{t(`nav.${item.key}`)}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="border-t p-4">
          <p className="text-xs text-muted-foreground">Super Admin Panel</p>
          <p className="text-xs text-muted-foreground">v0.1.0</p>
        </div>
      )}
    </aside>
  );
}
